/**
 * Shared mock data for workshop pages and components.
 *
 * Add new mock data here when it's reused across multiple workshop files.
 * For data used in only one file, define it at the top of that file instead.
 */

export interface MockUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: "owner" | "admin" | "member" | "guest";
}

export interface MockProject {
  id: string;
  name: string;
  description: string;
  spacesCount: number;
  createdAt: string;
  color: string;
  isPinned?: boolean;
}

export interface MockSpace {
  id: string;
  name: string;
  projectId: string;
  framesCount: number;
  color: string;
  isPinned?: boolean;
}

export interface MockFrame {
  id: string;
  type: "text" | "code" | "search" | "chat" | "browser" | "image";
  title: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MockAgent {
  id: string;
  name: string;
  status: "idle" | "thinking" | "executing" | "error";
  currentTask?: string;
}

// --- Users ---

export const MOCK_USERS: MockUser[] = [
  { id: "u1", name: "Alex Chen", email: "alex@denker.ai", role: "owner" },
  { id: "u2", name: "Maria Santos", email: "maria@denker.ai", role: "admin" },
  { id: "u3", name: "Jordan Lee", email: "jordan@denker.ai", role: "member" },
  { id: "u4", name: "Sam Rivera", email: "sam@example.com", role: "guest" },
];

export const MOCK_CURRENT_USER = MOCK_USERS[0];

// --- Projects ---

export const MOCK_PROJECTS: MockProject[] = [
  {
    id: "p1",
    name: "Marketing Website Redesign",
    description: "Complete overhaul of the marketing site with new branding",
    spacesCount: 4,
    createdAt: "2026-02-15T10:00:00Z",
    color: "#30D158",
  },
  {
    id: "p2",
    name: "Mobile App V2",
    description: "Native mobile app with offline support",
    spacesCount: 6,
    createdAt: "2026-01-20T14:30:00Z",
    color: "#5E5CE6",
  },
  {
    id: "p3",
    name: "Internal Dashboard",
    description: "Analytics and monitoring dashboard for the ops team",
    spacesCount: 2,
    createdAt: "2026-03-01T09:15:00Z",
    color: "#FF9F0A",
  },
];

// --- Spaces ---

export const MOCK_SPACES: MockSpace[] = [
  { id: "s1", name: "Research", projectId: "p1", framesCount: 8, color: "#30D158" },
  { id: "s2", name: "Design", projectId: "p1", framesCount: 12, color: "#5E5CE6" },
  { id: "s3", name: "Development", projectId: "p1", framesCount: 5, color: "#FF9F0A" },
  { id: "s4", name: "Launch", projectId: "p1", framesCount: 3, color: "#FF375F" },
  { id: "s5", name: "Sprint Planning", projectId: "p2", framesCount: 7, color: "#5E5CE6" },
  { id: "s6", name: "Feature Development", projectId: "p2", framesCount: 9, color: "#64D2FF" },
  { id: "s7", name: "Metrics", projectId: "p3", framesCount: 4, color: "#FF9F0A" },
];

// --- Frames ---

export const MOCK_FRAMES: MockFrame[] = [
  { id: "f1", type: "chat", title: "Agent Chat", x: 100, y: 100, width: 400, height: 500 },
  { id: "f2", type: "code", title: "api-handler.ts", x: 550, y: 80, width: 500, height: 600 },
  { id: "f3", type: "text", title: "Architecture Notes", x: 100, y: 650, width: 400, height: 300 },
  { id: "f4", type: "search", title: "Web Search", x: 1100, y: 100, width: 380, height: 450 },
  { id: "f5", type: "browser", title: "Preview", x: 1100, y: 600, width: 380, height: 350 },
];

// --- Agents ---

export const MOCK_AGENTS: MockAgent[] = [
  { id: "a1", name: "Claude", status: "executing", currentTask: "Refactoring auth module" },
  { id: "a2", name: "Research Agent", status: "idle" },
  { id: "a3", name: "Code Review Agent", status: "thinking", currentTask: "Reviewing PR #42" },
];
